import { useSyncExternalStore } from 'react'

/**
 * Whether a jump into the chat is under way (1.105.0) -- one flag for the
 * whole app, outside React's tree.
 *
 * From use, with a recording on the iPad: the day chosen from the map, and
 * for five seconds the map stood there without a sign, then the chat came.
 * React Router 7 makes every change of route a `startTransition`: React keeps
 * the old page on screen until the new one has drawn, and a long chat takes
 * seconds to draw on a tablet. The sign of 1.103.0 lived in the chat and so
 * could only appear once there was nothing left to wait for. The test bed
 * did not see it: its trip is small and the chat draws in milliseconds.
 *
 * So the jump is marked **at the tap** (`jumpBegins`), before the route
 * changes, and `Shell` shows the sign above every page. An update of an
 * external store is never part of a transition: React draws it at once, and
 * the running bar is a CSS animation that keeps moving while the chat is
 * drawn. It ends when the chat has landed or given up (`jumpEnds`, from
 * `useVerlaufsprung`), or when the chat is left.
 */
/**
 * What is under way: a jump to a post, or only the way into a chat
 * (1.110.0). From use, with a recording on the iPhone: the arrow back from
 * the trip's info page, and eight seconds of that page without a sign
 * before the chat came. The same transition as above, without `?m=`: the
 * sign of 1.105.0 only knew jumps to a post.
 */
export type JumpKind = 'post' | 'chat'

let running: JumpKind | null = null
const listeners = new Set<() => void>()
let safety: number | undefined

/**
 * The longest a sign may stand without anyone ending it: a read request
 * ends after 30 s (`api/client.ts`), and the chat ends the jump when it
 * lands, gives up or goes. This is only for a page that never comes.
 */
const MOST_MS = 45_000

/**
 * When the running jump began (1.195.1): the tap, before the chat page is
 * even drawn. The diagnosis line of `useVerlaufsprung` counts from here --
 * from use, a jump from the search stood fourteen seconds behind its bar,
 * and the line has to say where those went.
 */
let beganAt: number | null = null

export function jumpBeganAt(): number | null {
  return beganAt
}

function set(value: JumpKind | null) {
  if (running === value) return
  if (running === null && value !== null) beganAt = performance.now()
  running = value
  window.clearTimeout(safety)
  if (value !== null) safety = window.setTimeout(() => set(null), MOST_MS)
  listeners.forEach((listener) => listener())
}

export function jumpBegins(kind: JumpKind = 'post'): void {
  set(kind)
}

export function jumpEnds(): void {
  set(null)
}

/**
 * Marks the jump when an address carries `?m=` -- for every way into the
 * chat at a post: the calendar, the search, the photos, the polls, a
 * notification. Any other address passes untouched.
 */
export function jumpBeginsFor(url: string): void {
  const search = url.includes('?') ? url.slice(url.indexOf('?')) : ''
  if (new URLSearchParams(search).has('m')) jumpBegins()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useJumpRunning(): boolean {
  return useSyncExternalStore(subscribe, () => running !== null)
}

export function useJumpKind(): JumpKind | null {
  return useSyncExternalStore(subscribe, () => running)
}

/** The addresses of a chat: the trip's, a private one, the draft. */
export const CHAT_PATH = /^\/t\/\d+(\/with\/\d+|\/entwurf)?\/?$/

/**
 * Every tap on a link into another chat marks the way there (1.110.0) --
 * one listener for the whole app instead of a call at every link: the
 * arrows back from info, search, photos and polls, the tab on the map, a
 * trip in the list. A link to the chat already open marks nothing, and
 * neither does one to another post in it (`?m=` marks itself, and the
 * chat would never end a sign it did not mount for).
 *
 * In the capture phase, because `Link` prevents the default in its own
 * handler and React's handlers run before a listener on the document
 * would. A tap with a modifier opens a new tab and leaves this page alone.
 */
export function watchChatLinks(): () => void {
  const onClick = (event: MouseEvent) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null
    if (!(link instanceof HTMLAnchorElement)) return
    const url = new URL(link.href, window.location.href)
    if (url.origin !== window.location.origin || !CHAT_PATH.test(url.pathname)) return
    // A link that names a post (`?m=`) is a jump to it, also inside the
    // same chat (1.112.0, review): the polls page and the gallery used to
    // say so themselves at every link, now one place says it for all.
    if (url.searchParams.has('m')) {
      jumpBegins()
      return
    }
    if (url.pathname === window.location.pathname) return
    if (running === null) jumpBegins('chat')
  }
  document.addEventListener('click', onClick, true)
  return () => document.removeEventListener('click', onClick, true)
}
